import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import BrandHeader from '../../../components/common/BrandHeader';
import OutlineButton from '../../../components/common/OutlineButton';
import PrimaryButton from '../../../components/common/PrimaryButton';
import ResultHero from '../../../components/common/ResultHero';
import StepBar from '../../../components/common/StepBar';
import { PageShell } from '../../../components/common/Card';
import { rescheduleAppointment } from '../../../API/appointment';
import { fetchSpecialistsTimeSchedule } from '../../../API/specialist';
import {
  RESCHEDULE_COUNTER_PREFIX,
  RESCHEDULE_STEPS,
  RESCHEDULE_STEP_NUMBER,
} from '../../../constants/rescheduleConstants';
import { TENANT_TIMEZONE } from '../../../constants/business';
import { ROUTE_PREFIX_PRE_APPT } from '../../../constants/routes';
import {
  fetchVerifiedWorkflowById,
  markAppointmentBooked,
} from '../../../slices/workflow';
import { generateAppointmentType } from '../../../utils/bookingUtils';
import { getContactDetails } from '../../../utils/contactDetails';
import { clearPatientAccessToken } from '../../../utils/patientAccessStorage';
import {
  getRescheduleBlockedMessage,
  getRescheduleErrorMessage,
} from '../../../utils/rescheduleMessages';
import {
  buildCoverPayload,
  getAppointmentDurationMinutes,
  getCoverOnFile,
  getPaymentState,
} from '../../../utils/rescheduleUtils';
import {
  getCurrentMonth,
  getTimezoneFromAddress,
} from '../../../utils/timezoneUtils';
import { bookingTimeReducer } from '../../NewBooking/SelectDate/BookTimePicker/bookingTimeReducer';
import { ActionGroup } from '../NoApptOrPaymentInfoPage/styles';
import ConfirmDetails from './ConfirmDetails';
import ConfirmNewTime from './ConfirmNewTime';
import RescheduleDone from './RescheduleDone';
import SelectNewTime from './SelectNewTime';

const { TIME, DETAILS, CONFIRM, DONE } = RESCHEDULE_STEP_NUMBER;

// The chosen time is gone: back to the time step with fresh availability.
const PICK_ANOTHER_TIME_ERRORS = ['SLOT_TAKEN', 'START_TOO_SOON'];
const BLOCKED_TITLE = "This appointment can't be changed online";
// Retrying cannot help: the patient has to call. Mapped to the page title.
const FINAL_ERROR_TITLE = {
  NOT_ELIGIBLE: BLOCKED_TITLE,
  // The time has moved in Coreplus; only our records lag behind.
  WORKFLOW_UPDATE_FAILED: "We couldn't finish updating your booking",
  RESCHEDULE_CONFLICT: 'This appointment was just changed',
};

const toTelHref = (phone) => `tel:${phone.replace(/[^\d+]/g, '')}`;

const Reschedule = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { workflowId } = useParams();
  const workflow = useSelector(({ workflow }) => workflow.data);
  const { appointment, client, specialist, payment } = workflow || {};

  const contact = getContactDetails({ isAdhdPathway: workflow?.isAdhdPathway });
  const appointmentType = generateAppointmentType(
    Boolean(appointment?.isNewAppointment)
  );
  const durationMinutes =
    getAppointmentDurationMinutes(appointment) ||
    Number(specialist?.appointmentLength?.[`${appointmentType}Time`]) ||
    null;
  const paymentState = getPaymentState(payment);
  const [coverOnFile] = useState(() => getCoverOnFile(client?.medicare));

  const [step, setStep] = useState(TIME);
  const [timezone, setTimezone] = useState(() =>
    getTimezoneFromAddress(client?.addressResidential, {
      fallbackTimezone: TENANT_TIMEZONE,
    })
  );
  const [bookingTime, dispatchBookingTime] = useReducer(
    bookingTimeReducer,
    timezone,
    (initialTimezone) => ({
      month: getCurrentMonth(initialTimezone),
      date: null,
      time: null,
    })
  );
  const [availableTimeList, setAvailableTimeList] = useState([]);
  const [isScheduleLoaded, setIsScheduleLoaded] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleReloadKey, setScheduleReloadKey] = useState(0);
  const [coverDraft, setCoverDraft] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeStepError, setTimeStepError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [finalError, setFinalError] = useState(null);
  const [rescheduled, setRescheduled] = useState(null);

  const specialistId = specialist?.id;

  useEffect(() => {
    if (!specialistId || !timezone || !bookingTime.month) return undefined;

    let isCurrent = true;
    setIsScheduleLoaded(false);

    fetchSpecialistsTimeSchedule({
      specialistId,
      month: bookingTime.month,
      timezone,
      newOrReview: appointmentType,
      excludeWorkflowId: workflowId,
    }).then(({ data, error }) => {
      if (!isCurrent) return;
      setAvailableTimeList(data && !error ? data : []);
      setScheduleError(
        error ? getRescheduleErrorMessage('AVAILABILITY_FAILED', contact) : ''
      );
      setIsScheduleLoaded(true);
    });

    return () => {
      isCurrent = false;
    };
    // contact only feeds the error copy; refetching on it is not wanted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    specialistId,
    timezone,
    bookingTime.month,
    appointmentType,
    workflowId,
    scheduleReloadKey,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, finalError]);

  const backToAppointment = () =>
    history.push(`${ROUTE_PREFIX_PRE_APPT}/${workflowId}`);

  // The old zone's dates and slots no longer apply: clear them until the new zone loads.
  const handleTimezoneChange = (selectedTimezone) => {
    dispatchBookingTime({ type: 'clearSelection' });
    setAvailableTimeList([]);
    setIsScheduleLoaded(false);
    setTimezone(selectedTimezone);
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setConfirmError('');

    const result = await rescheduleAppointment(workflowId, {
      start: bookingTime.time.start,
      timezone,
      cover: buildCoverPayload(coverDraft, coverOnFile),
    });

    setIsSubmitting(false);

    if (result.data) {
      // The new time first, so "Back to my appointment" never shows the old one; then a
      // refresh for the rest (eligibility, payment).
      dispatch(
        markAppointmentBooked({
          workflowId,
          startDateTime: result.data.startDateTime,
          endDateTime: result.data.endDateTime,
        })
      );
      setRescheduled(result.data);
      setStep(DONE);
      dispatch(fetchVerifiedWorkflowById(workflowId));
      return;
    }

    if (result.httpStatus === StatusCodes.UNAUTHORIZED) {
      clearPatientAccessToken(workflowId);
      setConfirmError(getRescheduleErrorMessage('SESSION_EXPIRED', contact));
      return;
    }

    const message = getRescheduleErrorMessage(result.errorCode, contact);

    if (PICK_ANOTHER_TIME_ERRORS.includes(result.errorCode)) {
      // Step first: after an await React 17 renders each update on its own.
      setStep(TIME);
      setTimeStepError(message);
      dispatchBookingTime({ type: 'updateTime', time: null });
      // The old slots are stale: nothing is pickable until the reload lands.
      setAvailableTimeList([]);
      setIsScheduleLoaded(false);
      setScheduleReloadKey((key) => key + 1);
      return;
    }

    if (FINAL_ERROR_TITLE[result.errorCode]) {
      setFinalError({ title: FINAL_ERROR_TITLE[result.errorCode], message });
      return;
    }

    setConfirmError(message);
  };

  const reschedule = appointment?.reschedule;
  const isBlocked = !rescheduled && !reschedule?.eligible;
  // The details and confirm steps need a chosen time; without one, show the time step.
  const currentStep =
    (step === DETAILS || step === CONFIRM) && !bookingTime.time?.start
      ? TIME
      : step;

  const renderStopped = (title, message) => (
    <>
      <ResultHero tone="warning" title={title} subtitle={message} />
      <ActionGroup>
        <PrimaryButton href={toTelHref(contact.phone)}>
          {`Call ${contact.phone}`}
        </PrimaryButton>
        <OutlineButton onClick={backToAppointment}>
          Back to my appointment
        </OutlineButton>
      </ActionGroup>
    </>
  );

  const renderStep = () => {
    if (isBlocked) {
      return renderStopped(
        BLOCKED_TITLE,
        getRescheduleBlockedMessage(reschedule?.reason, contact)
      );
    }

    if (finalError) {
      return renderStopped(finalError.title, finalError.message);
    }

    switch (currentStep) {
      case DETAILS:
        return (
          <ConfirmDetails
            workflow={workflow}
            contact={contact}
            coverOnFile={coverOnFile}
            coverDraft={coverDraft}
            onBack={() => setStep(TIME)}
            onNext={(cover) => {
              setCoverDraft(cover);
              setConfirmError('');
              setStep(CONFIRM);
            }}
          />
        );
      case CONFIRM:
        return (
          <ConfirmNewTime
            workflow={workflow}
            contact={contact}
            newStart={bookingTime.time.start}
            timezone={timezone}
            durationMinutes={durationMinutes}
            paymentState={paymentState}
            isCoverChanged={Boolean(buildCoverPayload(coverDraft, coverOnFile))}
            isSubmitting={isSubmitting}
            errorMessage={confirmError}
            onBack={() => setStep(DETAILS)}
            onConfirm={handleConfirm}
          />
        );
      case DONE:
        return (
          <RescheduleDone
            workflow={workflow}
            contact={contact}
            // As patient-api saved it (business-zone offset); shown in the zone picked.
            newStartDateTime={rescheduled.startDateTime}
            timezone={timezone}
            durationMinutes={durationMinutes}
            paymentState={paymentState}
            onDone={backToAppointment}
          />
        );
      default:
        return (
          <SelectNewTime
            workflow={workflow}
            contact={contact}
            durationMinutes={durationMinutes}
            timezone={timezone}
            onTimezoneChange={handleTimezoneChange}
            bookingTime={bookingTime}
            dispatchBookingTime={(action) => {
              setTimeStepError('');
              dispatchBookingTime(action);
            }}
            availableTimeList={availableTimeList}
            isScheduleLoaded={isScheduleLoaded}
            scheduleError={scheduleError}
            errorMessage={timeStepError}
            onBack={backToAppointment}
            onNext={() => {
              setTimeStepError('');
              setStep(DETAILS);
            }}
          />
        );
    }
  };

  const showStepBar = !isBlocked && !finalError;

  return (
    <>
      <BrandHeader />
      <PageShell>
        {showStepBar && (
          <StepBar
            steps={RESCHEDULE_STEPS}
            currentStepNumber={currentStep}
            counterPrefix={RESCHEDULE_COUNTER_PREFIX}
            counterLabel={currentStep === DONE ? 'Complete' : undefined}
          />
        )}
        {renderStep()}
      </PageShell>
    </>
  );
};

export default Reschedule;
